import React, { useCallback, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FaLinkedin } from "react-icons/fa";
import mark from "./assets/mark.png";
import { useTheSeaContext } from "./TheSeaContext";
import StructuredData from "../StructuredData";
import "./the-sea.css";

export function useFocusAgent() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    if (location.pathname !== "/") {
      navigate("/");
    }
    setTimeout(() => {
      const panel = document.getElementById("agentPanel");
      const input = document.getElementById("chatInput");
      if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
      if (input) input.focus();
    }, 100);
  }, [navigate, location]);
}

function CookieConsent() {
  const { showCookieBanner, setCookieChoice } = useTheSeaContext();

  if (!showCookieBanner) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie choice">
      <p>We use essential cookies only to run this site. See our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Term of Use</Link> for details. Accepting is required to use the agent.</p>
      <div className="cookie-actions">
        <button className="btn btn-ghost" onClick={() => setCookieChoice("rejected")}>Decline</button>
        <button className="btn btn-primary" onClick={() => setCookieChoice("accepted")}>Accept</button>
      </div>
    </div>
  );
}

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/account", label: "My Account" },
];

export default function TheSeaLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const focusAgent = useFocusAgent();
  const navigate = useNavigate();
  const { isLoggedIn, accountName, logout } = useTheSeaContext();
  const firstName = accountName.trim().split(/\s+/)[0] || "user";

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    logout();
    closeMenu();
    navigate("/");
  };

  return (
    <div className="the-sea">
      <StructuredData />
      <header>
        <nav className="nav">
          <Link to="/" className="brand" onClick={closeMenu}>
            <img className="mark" src={mark} alt="askTheSea logo" />
            <div className="brand-text">
              <span className="brand-title">askTheSea</span>
              <span className="brand-sub">CMC Regulatory Intelligence</span>
            </div>
          </Link>
          <div className="navlinks" role="tablist" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className="navlink account-link"
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="nav-right">
            {isLoggedIn ? (
              <div className="account-menu">
                <Link to="/account" className="account-greeting">Hi, {firstName}</Link>
                <div className="account-menu-dropdown">
                  <button className="btn btn-logout" onClick={handleLogout}>Log Out</button>
                </div>
              </div>
            ) : (
              <Link to="/loginsignup" state={{ mode: "login" }} className="btn btn-ghost">Log In</Link>
            )}
            <button className="btn btn-primary" onClick={focusAgent}>askTheSea</button>
            <button
              className="hamburger"
              id="hamburgerBtn"
              aria-expanded={menuOpen}
              aria-label="Open menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </nav>
        <div className={"mobile-menu" + (menuOpen ? " open" : "")} id="mobileMenu">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="navlink account-link"
              onClick={closeMenu}
            >
              {item.label}
            </NavLink>
          ))}
          {isLoggedIn ? (
            <button className="btn btn-logout" onClick={handleLogout}>Log Out</button>
          ) : (
            <Link to="/loginsignup" state={{ mode: "login" }} className="btn btn-ghost" onClick={closeMenu}>Log In</Link>
          )}
          <button className="btn btn-primary" onClick={() => { closeMenu(); focusAgent(); }}>askTheSea</button>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer>
        <div className="wrap">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img className="mark" src={mark} alt="askTheSea logo" />
                askTheSea
              </div>
              <p>CMC regulatory intelligence built from publicly available information — monitored, distilled, and delivered. Not a substitute for professional advice, consultation, or decision-making.</p>
            </div>
            <div>
              <h4>Site</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/blog">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4>Contact</h4>
              <ul className="contact-list">
                <li><a href="mailto:info@askthesea.com">info@askthesea.com</a></li>
                <li><a href="#ask" onClick={(e) => { e.preventDefault(); focusAgent(); }}>askTheSea</a></li>
                <li><a className="linkedin-link" href="https://www.linkedin.com/company/askthesea-cmc-regulatory-intellence?trk=public_post_feed-actor-name" target="_blank" rel="noopener noreferrer"><FaLinkedin aria-hidden="true" /> LinkedIn</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2026 askTheSea. All rights reserved.</span>
            <span><Link to="/privacy">Privacy Policy</Link></span>
            <span><Link to="/terms">Terms of Service</Link></span>
            <span>SEA / CMC / INTEL</span>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}
